import { LUNAR_CYCLE_DAYS } from './moonIcon';

/**
 * Tekstowa nazwa fazy Księżyca (PL) na podstawie wieku i oświetlenia.
 * Nie zapisuje się w bazie — do użycia w CMS, stronach szczegółowych itd.
 *
 * Priorytet: progi na `illumination`, potem przedziały `moon_age` (dni od nowiu).
 */
export function getMoonPhaseName(moonAge: number, illumination: number): string {
  if (Number.isFinite(illumination) && illumination >= 99) {
    return 'Pełnia';
  }
  if (Number.isFinite(illumination) && illumination <= 1) {
    return 'Nów';
  }

  if (!Number.isFinite(moonAge)) {
    return '-';
  }

  if (moonAge < 1) {
    return 'Nów';
  }
  if (moonAge < 6.5) {
    return 'Sierp przybywający';
  }
  if (moonAge < 8) {
    return 'Pierwsza kwadra';
  }
  if (moonAge < 13.5) {
    return 'Garbaty przybywający';
  }
  if (moonAge < 15.5) {
    return 'Pełnia';
  }
  if (moonAge < 21) {
    return 'Garbaty ubywający';
  }
  if (moonAge < 23) {
    return 'Ostatnia kwadra';
  }
  return 'Sierp ubywający';
}

const MAIN_QUARTER_NAMES = new Set([
  'Nów',
  'Pierwsza kwadra',
  'Pełnia',
  'Ostatnia kwadra',
]);

/** Tylko cztery główne fazy (dla kalendarza); inaczej `null`. */
export function getMainQuarterPhaseName(
  moonAge: number,
  illumination: number,
): string | null {
  const n = getMoonPhaseName(moonAge, illumination);
  return MAIN_QUARTER_NAMES.has(n) ? n : null;
}

/** Docelowe `moon_age` (dni) dla kwadr przy idealnym cylindrycznym przybliżeniu. */
const MOON_AGE_FIRST_Q = LUNAR_CYCLE_DAYS / 4;
const MOON_AGE_LAST_Q = (3 * LUNAR_CYCLE_DAYS) / 4;

type QuarterRow = { date: string; moon_age: number; illumination: number };

/** Którego dnia w serii został „champion”; preferuj wcześniejszą datę przy remisie porównaniem tekstu ISO. */
function pickQuarterRunWinnerIndex(run: QuarterRow[], phase: string): number {
  let bestIdx = 0;
  const first = run[0];
  if (!first) return 0;

  if (phase === 'Pełnia') {
    for (let i = 1; i < run.length; i++) {
      const cand = run[i];
      const best = run[bestIdx];
      if (
        cand.illumination > best.illumination ||
        (cand.illumination === best.illumination && cand.date < best.date)
      ) {
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  if (phase === 'Nów') {
    for (let i = 1; i < run.length; i++) {
      const cand = run[i];
      const best = run[bestIdx];
      if (
        cand.illumination < best.illumination ||
        (cand.illumination === best.illumination && cand.date < best.date)
      ) {
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  const targetMoonAge =
    phase === 'Pierwsza kwadra' ? MOON_AGE_FIRST_Q : MOON_AGE_LAST_Q;
  let bestDist = Number.POSITIVE_INFINITY;
  bestIdx = 0;
  for (let i = 0; i < run.length; i++) {
    const d = Math.abs(run[i].moon_age - targetMoonAge);
    if (d < bestDist || (d === bestDist && run[i].date < run[bestIdx].date)) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Dla każdej doby wg `buildMoonPhaseRow` (Stan o południu Warszawa) klasyczne progi faz
 * z `getMoonPhaseName` są szerokie (np. `illumination >= 99` ⇒ Pełnia) — stąd
 * sąsiednie dwa dni mogą dostać tę samą nazwę głównej fazy, choć astrofazą jest jeden moment.
 *
 * Zwężamy wizualnie: w każdym ciągu ** kolejnych dni miesiąca ** z jednakową nazwą
 * kwadrantu zostaje etykieta tylko na ** jednym ** dniu (najbliższym astrofaze),
 * pozostałe — `null` (w kalendarzu wraca wtedy `%` oświetlenia).
 */
export function dedupeAdjacentQuarterPhaseLabels(rows: QuarterRow[]): Map<string, string | null> {
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));

  interface Ann extends QuarterRow {
    candidate: string | null;
  }

  const ann: Ann[] = sorted.map((r) => ({
    ...r,
    candidate: getMainQuarterPhaseName(r.moon_age, r.illumination),
  }));

  const out = new Map<string, string | null>();

  let i = 0;
  while (i < ann.length) {
    const c = ann[i].candidate;

    if (!c) {
      out.set(ann[i].date, null);
      i += 1;
      continue;
    }

    let j = i + 1;
    while (j < ann.length && ann[j].candidate === c) {
      j += 1;
    }

    const run = ann.slice(i, j);
    const winOffset = pickQuarterRunWinnerIndex(run, c);
    const winnerDate = run[winOffset].date;

    for (const r of run) {
      out.set(r.date, r.date === winnerDate ? c : null);
    }

    i = j;
  }

  return out;
}
